/**
 * InteraccionModal.js - Modal para agregar una nueva interaccion
 * Permite registrar llamadas, whatsapp, visitas o emails
 */
import React, { useState, useEffect } from 'react';
import { addInteraccion } from '../../localizador/services/placesService';

const tiposInteraccion = [
  { id: 'llamada', nombre: 'Llamada', icono: 'bi-telephone' },
  { id: 'whatsapp', nombre: 'WhatsApp', icono: 'bi-whatsapp' },
  { id: 'visita', nombre: 'Visita', icono: 'bi-geo-alt' },
  { id: 'email', nombre: 'Email', icono: 'bi-envelope' }
];

function getTodayDate() {
  return new Date().toISOString().split('T')[0];
}

function getCurrentTime() {
  return new Date().toTimeString().slice(0, 5);
}

export default function InteraccionModal({ show, onClose, negocioId, onInteraccionAgregada }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    tipo: 'llamada',
    fecha: getTodayDate(),
    hora: getCurrentTime(),
    notas: ''
  });

  // Reset form cuando se abre el modal
  useEffect(() => {
    if (show) {
      setFormData({
        tipo: 'llamada',
        fecha: getTodayDate(),
        hora: getCurrentTime(),
        notas: ''
      });
      setError(null);
    }
  }, [show]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.notas.trim()) {
      setError('Las notas son requeridas');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Construir fecha completa
      const fechaCompleta = new Date(`${formData.fecha}T${formData.hora}:00`).toISOString();

      const updatedNegocio = await addInteraccion(negocioId, {
        tipo: formData.tipo,
        fecha: fechaCompleta,
        notas: formData.notas.trim()
      });

      onInteraccionAgregada(updatedNegocio);

    } catch (err) {
      console.error('Error agregando interaccion:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="modal-backdrop fade show"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div
        className="modal fade show d-block"
        tabIndex="-1"
        role="dialog"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="bi bi-plus-circle me-2"></i>
                Nueva interaccion
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                disabled={loading}
              ></button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {/* Error */}
                {error && (
                  <div className="alert alert-danger py-2 mb-3">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {error}
                  </div>
                )}

                {/* Tipo de interaccion */}
                <div className="mb-3">
                  <label className="form-label">
                    Tipo <span className="text-danger">*</span>
                  </label>
                  <div className="d-flex flex-wrap gap-2">
                    {tiposInteraccion.map(tipo => (
                      <div className="form-check form-check-inline" key={tipo.id}>
                        <input
                          type="radio"
                          className="form-check-input"
                          id={`tipo-${tipo.id}`}
                          name="tipo"
                          value={tipo.id}
                          checked={formData.tipo === tipo.id}
                          onChange={() => handleChange('tipo', tipo.id)}
                          disabled={loading}
                        />
                        <label className="form-check-label" htmlFor={`tipo-${tipo.id}`}>
                          <i className={`bi ${tipo.icono} me-1`}></i>
                          {tipo.nombre}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Fecha y Hora */}
                <div className="row mb-3">
                  <div className="col-6">
                    <label className="form-label">
                      Fecha <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={formData.fecha}
                      onChange={(e) => handleChange('fecha', e.target.value)}
                      disabled={loading}
                      required
                    />
                    <small className="text-muted">Default: hoy</small>
                  </div>
                  <div className="col-6">
                    <label className="form-label">Hora</label>
                    <input
                      type="time"
                      className="form-control"
                      value={formData.hora}
                      onChange={(e) => handleChange('hora', e.target.value)}
                      disabled={loading}
                    />
                    <small className="text-muted">Default: ahora</small>
                  </div>
                </div>

                {/* Notas */}
                <div className="mb-3">
                  <label className="form-label">
                    Notas <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className="form-control"
                    rows={4}
                    value={formData.notas}
                    onChange={(e) => handleChange('notas', e.target.value)}
                    placeholder="Describe la interaccion..."
                    disabled={loading}
                    required
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={onClose}
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Guardando...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle me-2"></i>
                      Guardar
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
